<script setup lang="ts">
import { ref, computed } from "vue";
import { Button } from "./ui/button";

// Props & Emits for v-model + submit event

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submited', payload: { dateOfBirth: string; region: string }): void;
}>()

// // Reactive open state
const internalOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

// const open = ref(true);
const dateOfBirth = ref("");
const region = ref("");
const countryList = {
  US: "United States",
  PR: "Puerto Rico",
  MX: "Mexico",
  CA: "Canada",
  // Add more as needed
};

const formValid = computed(() => !!dateOfBirth.value && !!region.value);

function submitInfo() {
  if (!formValid.value) return;
  emit("submited", {
    dateOfBirth: dateOfBirth.value,
    region: region.value,
  });
  internalOpen.value = false;
}
</script>

<template>
  <div v-if="internalOpen" class="relative p-6">
    <!-- Close button -->
    <button
      class="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
      @click="internalOpen = false"
      aria-label="Close TOS"
    >
      ✕
    </button>

    <div class="w-full max-w-4xl max-h-[90vh] overflow-auto rounded-xl"></div>



      <div>
        <div class="text-2xl text-center text-primary">
          Rana Terms of Service
        </div>
      </div>

      <!-- Scrollable Content -->
      <div
        class="h-[65vh] px-4 pr-6 text-sm leading-relaxed space-y-4 overflow-y-auto"
      >
        <h2>Terms of Use</h2>
        <p>
          The following describes the terms of use of Rana LLC’s (hereafter
          “Rana” or “the Company”) software, services, and other aspects of the
          Rana ecosystem. Agreement to these terms is required in full in order
          to use any part of the Rana ecosystem including its products and
          services, up to the maximum extent made possible by applicable laws.
          The default jurisdiction under which these terms apply shall be the
          Commonwealth of Puerto Rico in the United States of America unless
          otherwise specified.
        </p>
        <h3 class="text-primary" id="your-info">Your Information</h3>

        <p>
          RanaEngine, RanaRemix, and the other locally installed parts of the
          Rana ecosystem have been designed with local-first and offline-first
          principles in order to keep end-user data private and to ensure that
          users have maximum control over their own data. Rana does not want
          your data, will not sell your data, will not disclose your data
          without legal obligation, does not profit from your data, and will not
          train machine learning models on your data without your permission.
          Your data is a liability that you are responsible for and Rana does
          not want it.
        </p>
        <p>
          For the cloud-enabled portions of the Rana ecosystem, limited
          identifiers may be used by the Company to provide such services and
          for authentication purposes with networked systems such as CloudFlare,
          Graph Universe, ActivityPub, Solid, and other connected systems, but
          only when necessary and only minimally. Please refer to the Privacy
          Policy for more details.
        </p>

        <h3 class="text-primary" id="age">Age Requirements</h3>
        <p>
          All users of any part of the Rana ecosystem, including its products,
          software, and services, must be 18 years of age or older. Illegal
          content is prohibited at all times across the Rana ecosystem, its
          software, and services in accordance with applicable laws.
        </p>

        <h3 class="text-primary" id="service-avail">
          Service Availability and Quality
        </h3>

        <p>
          Rana is a best-effort ecosystem designed to “fail-to-functional” and
          as such attempts to maintain the proper workings of as many features
          as possible despite the availability of the Internet, Graph Universe,
          Rana Network, or other services that the Rana ecosystem depends on.
          Once services are restored and become available again, Rana will
          attempt to synchronize all important data and refresh any stale data
          as appropriate across relevant nodes, clusters, and networks. The
          Company makes no guarantees as to the uptime, availability, integrity,
          persistence, speed, or quality by which our services operate, although
          we run our own company on these services and work hard to ensure
          optimal uptime not only for ourselves but for all users as well.
          Individual experiences may vary based on the conditions of local
          computing infrastructure, Internet connectivity, bandwidth, hardware
          performance, and similar factors, and may be outside the control of
          the Company.
        </p>

        <h3 class="text-primary">Your Rights and Responsibilities</h3>
        <p>
          Unless otherwise specified or restricted, all content produced by
          RanaEngine, publicly transmitted throughout the Rana ecosystem via
          servers controlled by Rana, or shared publicly across the Graph
          Universe, Solid, ActivityPub, or any other publicly integrated system
          with the Rana ecosystem, shall be considered public domain, fair-use,
          and
          <a
            href="https://creativecommons.org/publicdomain/zero/1.0/legalcode.en"
            target="_blank"
            rel="noopener noreferrer"
            >copyright-free</a
          >. Individuals and organizations that wish to retain restricted rights
          over their works as transmitted throughout the Rana ecosystem may do
          so via an Individual or Organizational license obtained through an
          appropriate subscription from
          <a href="https://rana.ai" target="_blank" rel="noopener noreferrer"
            >Rana.ai</a
          >. End-users agree to comply with all locally applicable laws, and are
          responsible for accurately maintaining and respecting the copyrights,
          trademarks, and other terms affecting content that they upload to the
          Rana ecosystem. In order to make this as easy and simple as possible,
          users are encouraged to use generative AI systems to produce and share
          copyright-free content whenever possible to avoid potential liability
          or violations due to copyright, trademark, or other
          content-restriction considerations in your local jurisdiction.
        </p>

        <h3 class="text-primary" id="rights">Rights You Give to Rana</h3>

        <p>
          Rana does not want your data, and we want to ensure that your private
          data stays private and protected by default, but that your public data
          is also free to easily distribute without encumbrance. Our primary
          goal is to promote the free sharing and exchange, remixing,
          translation, and adaptation of knowledge through open-protocols and
          systems, unencumbered by legal restrictions or centralized tracking
          and authorization systems. In support of this, we promote the use of
          copyright-free, public domain, and copyleft licenses by default for
          content that is created by our products and services, or publicly
          shared through our networks and systems, in support of the spirit of
          Fair-Use for all. For data that needs to remain private, we do
          everything we can to protect and secure such data for all users and
          organizations, including our free users, and offer tools and features
          to assist with this. As artificial intelligence and machine learning
          technologies are beginning to flood our world, we want to encourage a
          simple and straightforward approach to handling copyright around
          content: public things should be public, and private things should be
          private; complex licensing shackles end-users under the chains of
          strangers.
        </p>

        <h3 class="text-primary" id="limitation">
          Limitation of Liability and Indemnity
        </h3>
        <p>
          By using any product or service in the Rana ecosystem including
          RanaEngine, RanaRemix, RanaPod, and others, end-users voluntarily
          choose to assume complete responsibility for their own conduct and
          actions, and the consequences thereof, whether legal or otherwise.
          Rana cannot be held liable in any way for the actions users perform,
          the speech they utter, the messages they send, the data they transmit,
          the information they exchange, or anything else shared through the
          Rana ecosystem or its connected networks, responsibility lies
          exclusively with the end-users. Likewise, the Company cannot be held
          responsible for the consequences of end-user directives or
          instructions to autonomous agents, bots, crawlers, processes, jobs,
          tools, machine learning models, artificial intelligence systems, and
          similar technologies, or the consequences thereof, whether legal or
          otherwise in any jurisdiction. RanaEngine, RanaRemix, and similar
          locally-installed software from the Company shall be considered
          software products and not software services, despite any networking or
          connectivity features, and are not subject to the same requirements
          and restrictions as cloud-provided services or Software-as-a-Service
          systems. As locally-installed software products, end-users assume all
          responsibility and consequences for usage of such products, and agree
          to indemnify Rana from all liability related to their usage of such
          products, including coverage of all legal or court fees, fines,
          charges, penalties, and other applicable judgements levied against the
          Company as a result of end-user actions.
        </p>

        <h3 class="text-primary" id="content">Content Rights</h3>

        <p>
          It is our understanding that the
          <a
            href="https://www.copyright.gov/ai/"
            target="_blank"
            rel="noopener noreferrer"
            >United States Copyright Office has declared that artificial
            intelligences are not considered people</a
          >
          and cannot own copyrights. It is also our understanding that as a
          consequence, all content produced directly by generative AI systems
          can be considered public domain and fair-use, unless such content is
          restricted by the law for another reason such as trademark and other
          reasons. As such, the content created by machine learning models and
          AIs in the Rana ecosystem will default to a copyright-free and public
          domain license, but such content will not be disclosed to other
          parties until an end-user chooses to share it and will remain private.
        </p>

        <h3 class="text-primary" id="dmca">DMCA and Takedowns Policy</h3>

        <p>
          Rana cannot prevent the sharing of content that is not hosted on our
          own servers or cloud-services, and cannot be held responsible for the
          actions of end-users who run Rana software that is locally installed
          or self-hosted. For all parts of the Rana ecosystem that are
          locally-hosted or self-hosted by end-users such as RanaEngine,
          RanaPod, or similar software products, all DMCA takedown requests must
          be sent to such end-users directly and without the help of Rana; the
          Company cannot and will not act as a notifier, proxy, intermediary, or
          any other form of legal agent on behalf of anyone involving servers,
          systems, or running software that we do not control, and Rana cannot
          monitor end-user conduct or behavior while users run their own
          systems. End-users agree to indemnify Rana from all liability,
          charges, fees, fines, penalties, or other costs related to such
          actions taken against Rana as a result of activities performed by
          end-users.
        </p>

        <h3 class="text-primary" id="procedures">Notifications Procedures</h3>

        <p>
          For all parts of the Rana ecosystem that are hosted by Rana on the
          Company’s infrastructure such as a RanaPod hosted by Rana, if it can
          be determined that such infrastructure is hosting content in violation
          of another party’s rights, takedown requests may be delivered via
          email to <a href="mailto:dmca@rana.ai">dmca@rana.ai</a> and must
          include the following information:
        </p>
        <ul>
          <li>Rights holder legal contact email</li>
          <li>Type of Content Violation</li>
          <li>Unique identifier associated with the content in violation</li>
          <li>
            Publicly accessible address of the content in violation hosted by
            Rana
          </li>
          <li>
            Once provided with the information above, the Company will review
            all such claims and take action according to the results of an
            internal review on a first-come, first-serve basis. For successful
            takedown requests, notification will be provided to both the rights
            holder’s legal contact email and the end-user or organization
            responsible for the content violation, and such content will be
            temporarily removed.
          </li>
        </ul>

        <h3 class="text-primary" id="takedown">
          Temporary Takedown and Counter-Notice Procedures
        </h3>

        <p>
          If a DMCA takedown request is successful, violating parties will have
          5 business days to challenge the takedown request by filing a
          counter-notice with Rana at
          <a href="mailto:dmca@rana.ai">dmca@rana.ai</a> , and must provide the
          following information in such a counter-notice:
        </p>
        <ul>
          <li>Counter-notice User’s legal contact email</li>
          <li>Justification for Fair-Use of Content</li>
          <li>Unique identifier associated with the content in violation</li>
          <li>Publicly accessible address of the content in violation</li>
          <li>
            Once provided with a counter-notice including the information above,
            the Company will review the claim and then forward the
            counter-notice to the email of the rights holder that issued the
            takedown request. Rights holders will have 5 business days to
            respond to all such counter-notices, after which time any DMCA
            takedown requests will be reverted if a response is not received.
          </li>
        </ul>

        <h3 class="text-primary" id="additional-dmca">
          Additional DMCA Dispute Resolution
        </h3>

        <p>
          In the case of a continued challenge by a rights holder to a DMCA
          counter-notice, all such disputes must be handled by the individual
          parties and may not involve Rana, its employees, representatives, or
          anyone else that can be considered to be operating on behalf of the
          Company. Until the Company is presented with a legal opinion or ruling
          by a court involving the content in dispute, all such content will
          remain restricted from being publicly shared via Rana cloud services
          and systems where possible.
        </p>

        <h3 class="text-primary" id="dispute-res">
          Dispute Resolution and Governing Law
        </h3>

        <p>
          Rana works hard to equip our users with all of the tools necessary to
          manage their own data, content, and experiences throughout the Rana
          ecosystem and to the platforms and networks that connect to our
          products and services. We will do our best to avoid the need for any
          legal proceedings between ourselves and our users, but in the case
          that legal proceedings should be necessary to resolve a dispute, by
          using our products and services, end-users and organizations agree to
          settle all disputes with Rana via arbitration in the legal
          jurisdiction of San Juan in the Commonwealth of Puerto Rico in the
          United States, or at an alternate location of Rana’s choosing.
        </p>

        <h3 class="text-primary" id="service-limit">
          Service Limits and Rate Limiting
        </h3>

        <p>
          Rana is continually working to provide the best and most performant
          experiences to our users that is possible with our current
          technologies, and tries to offer accurate and reasonable predictions
          ahead of time when providing computing services, estimating levels of
          effort and time to completion for jobs and workflows, and when
          interoperating with network and connectivity features and functions.
          The technologies that the Rana ecosystem depend on in order to provide
          these functions and services are unpredictable, and may be affected by
          local conditions related to end-user hardware, network reliability,
          and other unknown factors outside of the Company’s control.
        </p>
        <p>
          Rana operates the Rana ecosystem, its products, and services on a
          best-effort basis, and cannot offer any form of Service Level
          Agreement in the tradition of regular cloud software companies. This
          limitation is made up by the ability for RanaEngine to operate using
          privately hosted infrastructure and in offline modes, so although the
          Company cannot guarantee the full functionality of all products and
          services all of the time, in practice a minimal level of functionality
          should be available at almost all times throughout the Rana ecosystem.
        </p>

        <h3 class="text-primary" id="no-warranty">No Warranty</h3>

        <p>
          ALL RANA SOFTWARE, INCLUDING RANAENGINE, RANAREMIX, RANAPOD,
          RANABRIDGE, AND ALL OTHER SOFTWARE AND DATA DELIVERED BY RANA IS
          PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT
          SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
          DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
          OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR
          THE USE OR OTHER DEALINGS IN THE SOFTWARE.
        </p>

        <h3 class="text-primary" id="payment">Payment and Billing</h3>

        <p>
          All payments and billing are pre-paid ahead of time before paid
          services are provided, with various subscription durations based on
          user choices. For on-demand services such as computing-on-demand,
          users must retain sufficient pre-purchased computing credits
          associated with their account in order to continue accessing on-demand
          services.
        </p>
        <p>
          All payments, purchases, and subscriptions provided by Rana will be
          handled and processed by Stripe, Inc. according to their
          <a
            href="https://stripe.com/legal/ssa"
            target="_blank"
            rel="noopener noreferrer"
            >terms as described here</a
          >.
        </p>

        <h3 class="text-primary" id="force-majeure">Force Majeure</h3>

        <p>
          Rana shall not be held liable for any disruptions or diminished
          capabilities of our products or services due to acts of God such as
          natural disasters including hurricanes, earthquakes, flooding,
          tornadoes, or any other similar disaster of nature, or any
          human-caused widespread societal disruption such as an electrical
          outage, protest, act of war, terrorism, uprising, embargo, sanction,
          hacking, denial-of-service, or other event resulting in the
          interruption of normal societal functions.
        </p>
        <p>
          In the event that a force majeure situation affecting paid services
          from the Company continues longer than 120 continuous days, either
          party may choose to terminate the remaining duration of the paid
          services, and the Company will refund the remaining portion of the
          pre-paid services on a prorated basis from the time of the request
          less the 120 day minimum period.
        </p>

        <h3 class="text-primary" id="no-agency">No Agency</h3>

        <p>
          No part of this Agreement shall be interpreted as creating a
          partnership, agency, joint venture, or other form of fiscal or legal
          relationship between Rana and any of its users, agents,
          representatives, associates, or other organizations.
        </p>

        <h3 class="text-primary" id="severability">Severability</h3>

        <p>
          If any part of this Agreement is held to be illegal, invalid, or
          inapplicable, all remaining portions of this Agreement shall remain
          enforceable in whole or in part, up to the fullest extent allowed by
          the law.
        </p>

        <h3 class="text-primary" id="third-party">
          No Third-Party Beneficiaries
        </h3>

        <p>
          No third-party may be a beneficiary of any part of this agreement,
          unless explicitly provided for by such terms herein. This includes
          creditors, partners, associates, and other parities that may have a
          financial or legal interest in any of the users or organizations
          utilizing the Rana ecosystem.
        </p>

        <h3 class="text-primary" id="survival">Survival</h3>

        <p>
          Rana cannot assist in recovering any data that we do not manage on our
          own systems and servers. In cases of survivorship, inheritance, or
          other court-ordered transfer of digital assets, Rana will assist with
          transferring the digital assets of a user’s or organization’s
          Rana-hosted RanaPod, adjusting any Rana-controlled DNS settings for
          the user, and claiming any pre-paid computing credits to a single
          third party legal representative of the user who will be responsible
          for any further distribution or disbursement.
        </p>
        <p>
          Such transfers may include remaining pre-paid computing credits stored
          in an account, files or data stored in an object store such as RanaPod
          on behalf of an account, or similar user-controlled data. Incidental
          data generated by user actions throughout the Rana ecosystem or data
          that the user cannot directly control are outside of the scope of this
          agreement, and will not be able to be transferred to new parties.
        </p>

        <h3 class="text-primary" id="governing">Governing Law</h3>

        <p>
          These terms are governed by the laws of the Commonwealth of Puerto
          Rico, United States.
        </p>

        <h3 class="text-primary" id="privacy">Privacy Policy</h3>

        <p>
          Rana LLC (hereafter referred to as “Rana” or “the Company”) works hard
          to minimize data collection and to preserve the privacy of our
          customers and their data. Each component of the Rana Ecosystem
          attempts to minimize the creation of data and identifiers that could
          be used to track, identify, or disambiguate end-users unless
          absolutely necessary. Since the technical details of each product and
          service in the Rana Ecosystem differ, please consult the table below
          for a summary of how this affects each of Rana’s products, services,
          and integrations.
        </p>

        <div class="flex w-full">
          <div class="table-wrapper">
            <table border="1">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Identifier</th>
                  <th>Exposure</th>
                  <th>3rd-Party Scripts</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>RanaEngine</td>
                  <td>Public Key</td>
                  <td>CloudFlare, GUN</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>RanaRemix</td>
                  <td>Public Key</td>
                  <td>CloudFlare, GUN</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>RanaPod</td>
                  <td>Public Key</td>
                  <td>Solid, GUN, ActivityPub</td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>Rana network</td>
                  <td>Login Cookie</td>
                  <td>CloudFlare</td>
                  <td>Stripe</td>
                </tr>
                <tr>
                  <td>Discord: Rana User App</td>
                  <td>Discord Login</td>
                  <td>Discord</td>
                  <td>Discord Server Dependent</td>
                </tr>
                <tr>
                  <td>Discord: Rana Server App</td>
                  <td>Discord Login</td>
                  <td>Discord</td>
                  <td>Discord Server Dependent</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <h3 class="text-primary" id="no-access">No Access to Your Data</h3>

        <p>
          RanaEngine runs locally in the sandbox of a user’s own web browser.
          The Company cannot access the data stored in a user’s browser at any
          time, including their Collections, Knowledge Graph, or any other parts
          of the product. The only visibility that the Company can have over
          end-user data is when such data is specifically sent to Rana.ai’s
          cloud services via CloudFlare, or is shared via another network such
          as GUN, ActivityPub, Solid, or another system offering public access
          to the data. All data in RanaEngine is private by default and will not
          be shared with anyone until a user manually alters these settings or
          copies such data to a shared or public location online.
        </p>

        <h3 class="text-primary" id="no-track">No Tracking or Surveillance</h3>

        <p>
          Rana does not want your data, does not want to track you, and works to
          minimize the data collected in the use of our products and services.
          Use of RanaEngine does not create or send any identifiers to the
          Company by default. This may change if a user chooses to use server
          resources provided by Rana such as a Rana GUN Relay, logging in to a
          Rana.ai account, connecting to a RanaPod hosted by Rana LLC, or
          otherwise engaging with Rana-provided cloud services.
        </p>

        <h3 class="text-primary" id="no-ad">
          No Advertising and No Selling User Data
        </h3>

        <p>
          Rana does not use digital advertising in our products and services,
          and it violates our principle of “maximize signal, minimize noise” by
          being disruptive to end-users state of mind. Machine learning makes it
          trivially easy to block, remove, and suppress advertising content
          anyway, so Rana is happy to be exploring other monetization methods
          instead such as subscriptions, sponsorships, partnerships, and other
          such opportunities instead of advertising while also preventing the
          disclosure of user data and user identity.
        </p>

        <h3 class="text-primary" id="local">
          Locally Installed, Works Offline, Logins Optional
        </h3>

        <p>
          Before high-speed Internet access became available, software used to
          be installed from physical media like disks or CDs locally onto a
          user’s hard drive. Applications could be launched immediately, without
          any obstacles such as a login screen, checking for Internet
          connectivity, or leaking private details in the background. By using
          Progressive Web App technology (PWAs), RanaEngine can be installed
          locally on a user’s devices while also gaining data persistence
          assurances to protect program data, and can once again provide the
          advantages of locally installed software while also gaining additional
          features when Internet connectivity is available. RanaEngine is
          locally installed browser software, similar to traditional desktop
          software, but loaded through a web browser for universal
          compatibility, ease of updates, and simple collaboration features. As
          locally installed software, end-users are entirely responsible for
          their use of the software and any actions taken, any content created
          or modified by use of the software, and any consequences thereof.
          End-users must comply with local laws and regulations, and Rana LLC
          cannot be held responsible and will not be liable for any actions
          taken by end-users including if such actions involve the use of
          RanaEngine, Rana servers, or any other product or service of the Rana
          ecosystem provided by Rana LLC.
        </p>

        <h3 class="text-primary" id="local-llm">
          Local Models = No Data Leakage
        </h3>

        <p>
          RanaEngine runs ML models locally on your own devices and hardware
          cluster, keeping your data safe from disclosure or repurposing by
          third parties including Rana LLC. Cloud-based AI services oftentimes
          use user data for fine-tuning or to train additional AI models at the
          expense of user privacy, so RanaEngine is designed to process end-user
          data safely only on hardware that you trust, such as the nodes in your
          own cluster or in collaboration with your chosen trusted associates
          using distributed, decentralized, and peer-to-peer connectivity
          options.
        </p>

        <h3 class="text-primary" id="peer">
          Peer-to-Peer, Decentralized, and Distributed Connectivity
        </h3>

        <p>
          The Rana ecosystem has been designed with Internet-enabled features
          using peer-to-peer, decentralized, and distributed techniques first
          and foremost, in order to preserve end-user privacy and to ensure the
          scalability of the Rana network. When RanaEngine connects to other
          installations of RanaEngine to build a cluster, the GUN decentralized
          network is used to resolve the connection between nodes, after which
          the nodes are able to communicate directly with each other using
          peer-to-peer techniques. A similar process is used when RanaEngine
          nodes controlled by different end-users communicate with each other as
          well. For transferring extremely large files such as machine learning
          models, distributed transport technologies such as WebTorrent may be
          used as well.
        </p>
        <p>
          In order to preserve end-user privacy while using peer-to-peer
          functionality, Rana recommends installing a VPN or proxy service such
          as the free
          <a href="https://1.1.1.1/" target="_blank" rel="noopener noreferrer"
            >1.1.1.1 from CloudFlare</a
          >
          using fully encrypted DNS and transport via the WARP protocol and
          malware filtering. Since Rana’s serverless cloud functionality is also
          hosted by the CloudFlare network, using 1.1.1.1 has the additional
          bonus of optimizing performance and throughput for the various
          features of the Rana ecosystem.
        </p>

        <h3 class="text-primary" id="min-analytics">Minimal Analytics</h3>

        <p>
          In order to provide cloud services and monitor the health of our
          network, traffic to Rana’s cloud servers will be logged, and roll-up
          and anonymized analytics created from such traffic for analysis, but
          this does not include the need to uniquely identify end-users by
          default. Local and offline usage of RanaEngine will not generate
          analytics by default, although users may choose to optionally share
          analytics data with Rana LLC at their own discretion via opt-in only
          functionality and with full data transparency. This includes the
          ability to share debug information with Rana developers via feedback
          forms with full transparency of the data being sent, and requiring
          end-user manual approval before sending.
        </p>

        <h3 class="text-primary" id="open-protocols">Open-Protocols</h3>

        <p>
          The Internet never would have been able to scale as far and as quickly
          as it did without open protocols. We have seen the limitations and
          trade-offs of walled gardens, it’s time to return to open-protocols,
          easy sharing, and widely replicated, human-curated knowledge. We had a
          taste of this in the early Web 2.0 era with RSS Feeds, open-access
          REST APIs, and content mashups, but this golden age disappeared
          all-too-quickly and such data streams became locked behind logins and
          paywalls.
        </p>
        <p>
          Rana uses open-protocols throughout our ecosystem, including Solid
          Protocol for storage, ActivityPub for social networking, and GUN for
          connectivity and synchronization, all of which are freely accessible
          to other developers without special permissions or gatekeeping, and
          can easily be extended and repurposed for other uses. Rana is
          committed to continuing to use open-protocols whenever possible moving
          forward as standards and technologies improve, in order to maximize
          the utility of our ecosystem.
        </p>

        <h3 class="text-primary" id="source-access">
          Source-Accessible, Local-First
        </h3>

        <p>
          One of the most beautiful parts of the architecture of the web is that
          web pages are always rendered locally, directly in a user’s browser.
          This ensures that the source code of every page is also directly
          accessible to end-users, for examination, modification, and adaptation
          to different browser environments on different devices. All parts of
          the Rana ecosystem including RanaEngine that run directly in the
          browser and are local-first may have their source code examined by
          these same browsers, and will always remain transparent to end-users.
          Rana also integrates and extends many different open-source libraries
          and projects to power our core functionality, and we make these
          open-source components freely available to the world in our GitHub
          account at:
          <a
            href="https://github.com/orgs/RanaEngine/repositories"
            target="_blank"
            rel="noopener noreferrer"
            >https://github.com/orgs/RanaEngine/repositories</a
          >
        </p>
        <p>
          For the cloud-hosted services in the Rana ecosystem, Rana LLC reserves
          the right to selectively disclose the source code of the underlying
          technologies at our discretion, while working hard to remain
          compatible with open protocols and standards whenever possible.
        </p>

        <h3 class="text-primary" id="open-source">Open-Source Licenses</h3>

        <p>Here is a table of the open-source software that Rana depends on:</p>

        <div class="flex w-full">
          <div class="table-wrapper">
            <table border="1">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>License</th>
                  <th>Repository</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <a
                      href="https://gun.eco"
                      target="_blank"
                      rel="noopener noreferrer"
                      >GUN</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/amark/gun/blob/master/LICENSE.md"
                      target="_blank"
                      rel="noopener noreferrer"
                      >MIT</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/amark/gun"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://github.com/amark/gun</a
                    >
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://webllm.mlc.ai/"
                      target="_blank"
                      rel="noopener noreferrer"
                      >WebLLM</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/mlc-ai/web-llm/blob/main/LICENSE"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Apache 2.0</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/mlc-ai/web-llm"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://github.com/mlc-ai/web-llm</a
                    >
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://huggingface.co/docs/transformers.js/index"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Transformers.js</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/xenova/transformers.js/blob/main/LICENSE"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Apache 2.0</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/xenova/transformers.js"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://github.com/xenova/transformers.js</a
                    >
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://tailwindcss.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Tailwindcss</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/tailwindlabs/tailwindcss/blob/next/LICENSE"
                      target="_blank"
                      rel="noopener noreferrer"
                      >MIT</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/tailwindlabs/tailwindcss"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://github.com/tailwindlabs/tailwindcss</a
                    >
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://vueflow.dev/"
                      target="_blank"
                      rel="noopener noreferrer"
                      >VueFlow</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/bcakmakoglu/vue-flow/blob/master/LICENSE"
                      target="_blank"
                      rel="noopener noreferrer"
                      >MIT</a
                    >
                  </td>
                  <td>
                    <a
                      href="https://github.com/bcakmakoglu/vue-flow"
                      target="_blank"
                      rel="noopener noreferrer"
                      >https://github.com/bcakmakoglu/vue-flow</a
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <br />

        <h2 class="text-primary" id="community-guidelines">
          Community Guidelines
        </h2>

        <h3 class="text-primary">Disinformation</h3>

        <p>
          Rana LLC does not condone the use of our products or services for the
          spreading of disinformation, misinformation, malformation, or any
          other form of information that could be harmful to others. In order to
          minimize this risk, Rana provides tools to help users control their
          own experiences with our products and services. In no case will Rana
          be held liable for the information users create or spread through our
          products and services, or with those services that may be connected to
          our servers or systems such as the Fediverse, GUN Network, or via any
          other protocols.
        </p>

        <h3 class="text-primary">Defamation</h3>

        <p>
          Rana will not be held liable for any claims of defamation, damage to
          character or public image, loss of face, or any other form of mental,
          emotional, or social disruption or disturbance. All parties using the
          Rana ecosystem are responsible for their own actions, and Rana LLC
          will not be held responsible for the moderation of any speech or
          information maintained in digital spaces that is not directly
          controlled by Rana LLC, even if our tools are involved in creation,
          modification, or distribution of offending content. It it not possible
          for the Company to control what information is transmitted over
          distributed, decentralized, and peer-to-peer networks, so any claims
          related to this are between the party claiming injury and the end-user
          only, not Rana LLC.
        </p>

        <h3 class="text-primary">Violence</h3>

        <p>
          Any illegal speech that promotes, instigates, or calls for physical
          violence against other people, groups, public infrastructure, or
          similarly malicious behavior is strictly prohibited throughout the
          Rana ecosystem. Rana LLC will cooperate with relevant law enforcement
          to pursue such cases whenever brought to our attention. Incidents
          requiring such follow-up should be reported to incidents@rana.ai and
          will be reviewed privately by internal teams.
        </p>

        <h3 class="text-primary">Harassment</h3>

        <p>
          The Rana ecosystem is composed primarily of decentralized,
          distributed, and peer-to-peer (P2P) functionality that prevents the
          centralized forms of moderation that purely cloud-hosted services
          provide. As such, Rana has been designed with tools and features to
          help end-users protect and maintain their own wellbeing, without the
          need for centralized arbiters in most cases.
        </p>
        <p>
          By providing these tools to our end-users, we equip them with a much
          more powerful means to preserve the integrity of their own experiences
          and to handle conflict with other users. This approach scales with
          every new user of the Rana ecosystem, unlike with centrally controlled
          systems that become overwhelmed as more users join the system.
        </p>
        <p>
          Rana recommends that users block or mute troublemakers early and
          often, or use an AI chat agent to help filter the content you are
          exposed to before it becomes personally disruptive. In order to help
          streamline these “bottom-up” filters and preferences, Rana makes it
          easy to share such settings with others so that networks of trust can
          help each other preserve rewarding experiences without having to spend
          a lot of time or energy on the trolls.
        </p>
        <p>
          For social features in the Rana ecosystem where moderator roles are
          present, all claims of harassment that do not cross the line into
          creating an Incident regarding threats of violence or illegal abuse
          materials must be handled by the relevant moderators. For best
          results, Rana recommends simply blocking trolls and sharing your block
          lists with your friends, since it is only possible to change these
          settings for yourself in a peer-to-peer system.
        </p>
        <p>
          Rana also recommends the use of a Virtual Private Network to protect
          your IP address from being improperly disclosed during peer-to-peer
          connections, and in particular we recommend using CloudFlare WARP via
          the
          <a href="https://1.1.1.1/" target="_blank" rel="noopener noreferrer"
            >1.1.1.1. app available for free here</a
          >.
        </p>

        <h3 class="text-primary">Nudity</h3>

        <p>
          Nudity, pornography, and other sexually explicit materials are
          prohibited on publicly-facing properties and services hosted by Rana
          LLC. Rana cannot control materials that are privately held or
          distributed by our users if they are hosting or distributing such
          materials themselves using their own equipment and means using
          distributed, decentralized, or peer-to-peer systems.
        </p>
        <p>
          For users that choose to store shareable content on Rana-hosted cloud
          infrastructure such as RanaPod, all such materials will be
          automatically scanned for Child Sexual Abuse Materials using automated
          means, and any detections will result in the creation of an Incident
          that will be followed up according to
          <a
            href="https://developers.cloudflare.com/cache/reference/csam-scanning/#what-happens-when-a-match-is-detected"
            target="_blank"
            rel="noopener noreferrer"
            class="terms-anchor"
            >procedures described here</a
          >. This may result in legal liability for users who engage in such
          conduct, and Rana LLC will comply with all law enforcement
          investigations regarding such abusive materials.
        </p>

        <h3 class="text-primary">Not-Safe-For-Work (NSFW) Content</h3>

        <p>
          Any content that can be considered excessively violent, gross, gory,
          sexually suggestive, or would otherwise be psychologically disturbing
          to hypothetical average adult Internet users should be marked
          Not-Safe-For-Work (NSFW) in the Rana ecosystem and in connected
          services such as Discord.
        </p>
        <p>
          Rana LLC will not engage with or be involved in any such disputes over
          content decency, and end-users are provided with powerful tools for
          self-filtering according to their own tastes and preferences
          throughout our systems that can be shared with others to make it easy
          to curate your own experience. These limitations do not apply to
          illegal materials, which will be followed up as an Incident according
          to the procedures previously described.
        </p>

        <h3 class="text-primary">Copyright</h3>

        <p>
          All users of the Rana ecosystem agree to abide by all applicable
          copyright laws related to the content created, accessed, or
          distributed through the Rana ecosystem. In order to streamline this,
          all content created by machine learning models or via artificial
          intelligence systems in Rana will default to copyright-free and
          public-domain licenses,
          <a
            href="https://www.copyright.gov/ai/"
            target="_blank"
            rel="noopener noreferrer"
            class="terms-anchor"
            >in accordance with these rulings</a
          >.
        </p>
        <p>
          Additional features related to licensing are included throughout
          various Rana tools and services, but end-users are fully responsible
          for their own compliance or lack thereof related to copyrighted
          content, and Rana cannot be held liable for the actions of its users
          regarding copyright violations. Additional details may be found in the
          DMCA and Takedowns Policy section of the Terms of Use.
        </p>
      </div>

      <!-- Footer Form -->
      <div
        class="sticky bottom-0 left-0 flex flex-col items-start w-full p-4 space-y-4 border-t bg-background"
      >
        <div class="w-full space-y-4">
          <!-- Date of Birth -->
          <label class="block w-full text-sm font-medium">
            Date of Birth
            <input
              type="date"
              v-model="dateOfBirth"
              class="w-full px-3 py-2 mt-1 border rounded bg-background text-foreground border-border"
            />
          </label>

          <!-- Region -->
          <label class="block w-full text-sm font-medium">
            Region
            <select
              v-model="region"
              class="w-full px-3 py-2 mt-1 border rounded bg-background text-foreground border-border"
            >
              <option value="" disabled>Select your region</option>
              <option
                v-for="(name, code) in countryList"
                :key="code"
                :value="code"
              >
                {{ name }}
              </option>
            </select>
          </label>

          <Button class="w-full" :disabled="!formValid" @click="submitInfo">
            Accept and Continue
          </Button>
        </div>
      </div>
    </div>
  
</template>
